import { parseFieldSource } from '../configurable-step/fieldSource';
import { FORBIDDEN_KEYS } from '../interpolation/forbiddenKeys';
import type {
  BindingDefaults,
  BindingFieldSource,
  EventBinding,
} from './types';

const BINDING_CAP = 10;
const BINDING_ID = /^[a-z][a-z0-9-]{0,63}$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function readFieldSource(value: unknown): BindingFieldSource | undefined {
  // A bare string is a dashboard data path. Bindings require the object form,
  // and that object is exactly one of dataPath or literal.
  if (typeof value === 'string' || !isPlainObject(value)) return undefined;
  const keys = Object.keys(value).filter((key) => !FORBIDDEN_KEYS.has(key));
  if (keys.length !== 1) return undefined;
  const parsed = parseFieldSource(value);
  if (parsed.kind === 'dataPath') return { dataPath: parsed.dataPath };
  if (parsed.kind === 'literal') return { literal: parsed.literal };
  return undefined;
}

function readFieldMap(
  value: unknown,
): Readonly<Record<string, BindingFieldSource>> | undefined {
  if (value === undefined) return {};
  if (!isPlainObject(value)) return undefined;
  const fields: Record<string, BindingFieldSource> = {};
  for (const key of Object.keys(value)) {
    if (FORBIDDEN_KEYS.has(key) || key.length === 0) continue;
    const source = readFieldSource(value[key]);
    if (!source) continue;
    fields[key] = source;
  }
  return fields;
}

function readOne(
  value: unknown,
  seenIds: ReadonlySet<string>,
): EventBinding | undefined {
  if (!isPlainObject(value)) return undefined;
  const { id, on, event } = value;
  if (typeof id !== 'string' || !BINDING_ID.test(id) || seenIds.has(id)) {
    return undefined;
  }
  if (typeof on !== 'string' || on.length === 0) return undefined;
  if (typeof event !== 'string' || event.length === 0) return undefined;
  const fields = readFieldMap(value.fields);
  const customDimensions = readFieldMap(value.customDimensions);
  if (!fields || !customDimensions) return undefined;
  return {
    id,
    on,
    event,
    ...(Object.keys(fields).length > 0 ? { fields } : {}),
    ...(Object.keys(customDimensions).length > 0 ? { customDimensions } : {}),
  };
}

/**
 * Valid bindings on a tracking slice. The argument is `_meta.tracking`.
 * Anything past the 10th array entry is ignored. A bad entry is dropped
 * and does not throw.
 */
export function readBindings(meta: unknown): EventBinding[] {
  if (!isPlainObject(meta) || !Array.isArray(meta.events)) return [];
  const seenIds = new Set<string>();
  const kept: EventBinding[] = [];
  for (const raw of meta.events.slice(0, BINDING_CAP)) {
    const binding = readOne(raw, seenIds);
    if (!binding) continue;
    seenIds.add(binding.id);
    kept.push(binding);
  }
  return kept;
}

/** True when the slice carries a `mount` or `view` binding. */
export function hasLifecycleBinding(meta: unknown): boolean {
  return readBindings(meta).some(
    (binding) => binding.on === 'mount' || binding.on === 'view',
  );
}

/** `'keep'` and `'suppress'` only. Any other value is dropped. */
export function readDefaults(meta: unknown): BindingDefaults {
  if (!isPlainObject(meta) || !isPlainObject(meta.defaults)) return {};
  const defaults: Record<string, 'keep' | 'suppress'> = {};
  for (const key of Object.keys(meta.defaults)) {
    if (FORBIDDEN_KEYS.has(key)) continue;
    const value = meta.defaults[key];
    if (value === 'keep' || value === 'suppress') defaults[key] = value;
  }
  return defaults;
}
